/**
 * Role Service
 * Handles role management operations with backend API
 */

import { apiGet, apiPost, apiPut, apiDelete } from '@/shared/api/apiClient';
import type { PagedResult } from '@/types';

/**
 * Role DTO matching backend RoleDto
 */
export interface RoleDto {
  id: string;
  name: string;
  level: number;
  tenantId: string;
  createdAt: string;
  updatedAt?: string | null;
}

/**
 * Create Role Request
 */
export interface CreateRoleRequest {
  name: string;
  level: number;
  tenantId: string;
  userId: string;
}

/**
 * Update Role Request
 */
export interface UpdateRoleRequest {
  name: string;
  level: number;
  tenantId: string;
  userId: string;
}

/**
 * Get all roles for a tenant
 */
export async function getAllRoles(tenantId: string): Promise<RoleDto[]> {
  return apiGet<RoleDto[]>(`/roles?tenantId=${tenantId}`);
}

export interface RoleListParams {
  tenantId: string;
  /** 0-based row offset. */
  offset?: number;
  limit?: number;
  search?: string;
}

export interface RoleListResult {
  items: RoleDto[];
  totalCount: number;
}

/**
 * Server-side paginated/searchable variant of `getAllRoles`, used only by the
 * Configuration screen's table. `getAllRoles` above is left untouched — it still
 * serves dropdown selects and the permission-matrix role picker unpaginated.
 */
export async function getRolesPaged(params: RoleListParams): Promise<RoleListResult> {
  const query = new URLSearchParams({ tenantId: params.tenantId });
  if (params.offset !== undefined) query.append('offset', String(params.offset));
  if (params.limit !== undefined) query.append('limit', String(params.limit));
  if (params.search) query.append('search', params.search);

  const data = await apiGet<PagedResult<RoleDto>>(`/roles?${query.toString()}`);
  return { items: data.items ?? [], totalCount: data.totalCount ?? 0 };
}

/**
 * Get a role by ID
 */
export async function getRoleById(id: string, tenantId: string): Promise<RoleDto> {
  return apiGet<RoleDto>(`/roles/${id}?tenantId=${tenantId}`);
}

/**
 * Create a new role
 */
export async function createRole(data: CreateRoleRequest): Promise<{ id: string }> {
  return apiPost<{ id: string }>('/roles', data);
}

/**
 * Update an existing role
 */
export async function updateRole(id: string, data: UpdateRoleRequest): Promise<void> {
  return apiPut<void>(`/roles/${id}`, data);
}

/**
 * Delete a role
 */
export async function deleteRole(id: string, tenantId: string, userId: string): Promise<void> {
  return apiDelete<void>(`/roles/${id}`, { tenantId, userId });
}